<template>
  <div class="row mt-3">
    <!-- New sign-ups trend -->
    <div class="col-md-6 mb-4">
      <div class="hc-card">
        <div class="p-3">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0 font-weight-bold">New Sign-ups</h6>
            <small class="text-muted">Last 12 weeks</small>
          </div>
          <div v-if="chartsLoading.signups" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-secondary"></div>
          </div>
          <div v-else-if="signupsChartData" style="position:relative;height:180px;">
            <bar-chart :data="signupsChartData" :options="barOptions" />
          </div>
        </div>
      </div>
    </div>

    <!-- Orders per day -->
    <div class="col-md-6 mb-4">
      <div class="hc-card">
        <div class="p-3">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0 font-weight-bold">Orders per Day</h6>
            <small class="text-muted">Last 30 days</small>
          </div>
          <div v-if="chartsLoading.orders" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-secondary"></div>
          </div>
          <div v-else-if="ordersChartData" style="position:relative;height:180px;">
            <line-chart :data="ordersChartData" :options="lineOptions" />
          </div>
        </div>
      </div>
    </div>

    <!-- Orders by hour (today) -->
    <div class="col-md-6 mb-4">
      <div class="hc-card">
        <div class="p-3">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0 font-weight-bold">Orders by Hour</h6>
            <small class="text-muted">Today</small>
          </div>
          <div v-if="chartsLoading.hourly" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-secondary"></div>
          </div>
          <div v-else-if="hourlyChartData" style="position:relative;height:180px;">
            <bar-chart :data="hourlyChartData" :options="hourlyOptions" />
          </div>
        </div>
      </div>
    </div>

    <!-- Plan distribution -->
    <div class="col-md-4 mb-4">
      <div class="hc-card">
        <div class="p-3">
          <h6 class="mb-3 font-weight-bold">Plan Distribution</h6>
          <div v-if="!planDistribution || planDistribution.length === 0" class="text-center text-muted py-4">
            <small>No data</small>
          </div>
          <div v-else style="position:relative;height:200px;">
            <doughnut-chart :data="planChartData" :options="doughnutOptions" />
          </div>
        </div>
      </div>
    </div>

    <!-- Revenue trend -->
    <div class="col-md-8 mb-4">
      <div class="hc-card">
        <div class="p-3">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0 font-weight-bold">Subscription Revenue Trend</h6>
            <small class="text-muted">Last 6 months</small>
          </div>
          <div v-if="chartsLoading.revenue" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-secondary"></div>
          </div>
          <div v-else-if="revenueChartData" style="position:relative;height:180px;">
            <line-chart :data="revenueChartData" :options="revenueOptions" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { Line as LineChart, Bar as BarChart, Doughnut as DoughnutChart } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Title, Tooltip, Legend,
  BarElement, CategoryScale, LinearScale,
  PointElement, LineElement,
  ArcElement, Filler,
} from 'chart.js';

ChartJS.register(
  Title, Tooltip, Legend,
  BarElement, CategoryScale, LinearScale,
  PointElement, LineElement,
  ArcElement, Filler
);

const PLAN_COLORS = [
  '#4e73df', '#1cc88a', '#36b9cc', '#f6c23e',
  '#e74a3b', '#858796', '#5a5c69', '#2e59d9',
];

export default {
  name: 'AdminChartsSection',
  components: { LineChart, BarChart, DoughnutChart },

  props: {
    planDistribution: { type: Array, default: () => [] },
  },

  data() {
    return {
      chartsLoading: { signups: true, orders: true, revenue: true, hourly: true },
      signupsRaw: null,
      ordersRaw: null,
      revenueRaw: null,
      hourlyRaw: null,
    };
  },

  computed: {
    signupsChartData() {
      if (!this.signupsRaw) return null;
      return {
        labels: this.signupsRaw.labels,
        datasets: [{
          label: 'New Companies',
          data: this.signupsRaw.data,
          backgroundColor: 'rgba(78,115,223,0.8)',
          borderRadius: 4,
        }],
      };
    },

    ordersChartData() {
      if (!this.ordersRaw) return null;
      return {
        labels: this.ordersRaw.labels,
        datasets: [{
          label: 'Orders',
          data: this.ordersRaw.data,
          borderColor: '#1cc88a',
          backgroundColor: 'rgba(28,200,138,0.1)',
          tension: 0.3,
          fill: true,
          pointRadius: 2,
        }],
      };
    },

    planChartData() {
      if (!this.planDistribution || !this.planDistribution.length) return null;
      return {
        labels: this.planDistribution.map(p => p.plan),
        datasets: [{
          data: this.planDistribution.map(p => p.count),
          backgroundColor: PLAN_COLORS.slice(0, this.planDistribution.length),
          borderWidth: 2,
          borderColor: '#fff',
        }],
      };
    },

    revenueChartData() {
      if (!this.revenueRaw) return null;
      return {
        labels: this.revenueRaw.labels,
        datasets: [{
          label: 'KES',
          data: this.revenueRaw.data,
          borderColor: '#4e73df',
          backgroundColor: 'rgba(78,115,223,0.1)',
          tension: 0.4,
          fill: true,
          pointRadius: 3,
        }],
      };
    },

    barOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
      };
    },

    lineOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
      };
    },

    doughnutOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } },
      };
    },

    hourlyChartData() {
      if (!this.hourlyRaw) return null;
      return {
        labels: this.hourlyRaw.labels,
        datasets: [{
          label: 'Orders',
          data: this.hourlyRaw.data,
          backgroundColor: this.hourlyRaw.data.map((_, i) => {
            const now = new Date().getHours();
            return i === now ? 'rgba(78,115,223,1)' : 'rgba(78,115,223,0.4)';
          }),
          borderRadius: 3,
        }],
      };
    },

    hourlyOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { ticks: { maxTicksLimit: 12 } },
          y: { beginAtZero: true, ticks: { precision: 0 } },
        },
      };
    },

    revenueOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          y: {
            beginAtZero: true,
            ticks: { callback: v => 'KES ' + Number(v).toLocaleString() },
          },
        },
      };
    },
  },

  mounted() {
    this.fetchCharts();
  },

  methods: {
    async fetchCharts() {
      await Promise.all([
        this.fetchSignups(),
        this.fetchOrders(),
        this.fetchRevenue(),
        this.fetchHourly(),
      ]);
    },

    async fetchSignups() {
      try {
        const { data } = await axios.get('/api/admin/charts/signups-trend', { params: { weeks: 12 } });
        this.signupsRaw = data;
      } finally {
        this.chartsLoading.signups = false;
      }
    },

    async fetchOrders() {
      try {
        const { data } = await axios.get('/api/admin/charts/orders-trend', { params: { days: 30 } });
        this.ordersRaw = data;
      } finally {
        this.chartsLoading.orders = false;
      }
    },

    async fetchRevenue() {
      try {
        const { data } = await axios.get('/api/admin/charts/revenue-trend', { params: { months: 6 } });
        this.revenueRaw = data;
      } finally {
        this.chartsLoading.revenue = false;
      }
    },

    async fetchHourly() {
      try {
        const { data } = await axios.get('/api/admin/charts/orders-by-hour');
        this.hourlyRaw = data;
      } finally {
        this.chartsLoading.hourly = false;
      }
    },

    refresh() {
      this.chartsLoading = { signups: true, orders: true, revenue: true, hourly: true };
      this.fetchCharts();
    },
  },
};
</script>

<style scoped>
.hc-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 14px;
}
</style>
